import { GameBySlugRequestDto } from '@/services/dto/games/game-by-slug-request.dto';
import { GamesQueryKeys } from '../../queryKeys';
import { useQuery } from '@tanstack/react-query';
import { fetchGameBySLug } from './service';
import { Game } from '@/models/game';
import { AxiosError } from 'axios';
import { GenericError } from '@/models/generic-error';
import { UseGameBySlugProps } from './props';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { useSystemConfiguration } from '@/hooks/theme/useSystemConfiguration';
import { Either } from '@starsoft/common/interfaces';
import { GamingAggregatorStatusId } from '@starsoft/common/models/gaming-aggregator/status/id';

export function useGameBySlug(options?: UseGameBySlugProps) {
  const { slug, refetchOnMount = true } = options ?? {};
  const { push } = useRouter();
  const { aggregators } = useSystemConfiguration();
  const queryKey: [GamesQueryKeys.BySlug, GameBySlugRequestDto] = [
    GamesQueryKeys.BySlug,
    {
      slug: slug as string,
    },
  ];

  const { data, isPending, error, refetch, fetchStatus, isError } = useQuery<
    Game,
    AxiosError<GenericError>,
    Game,
    [GamesQueryKeys.BySlug, GameBySlugRequestDto]
  >({
    queryKey,
    queryFn: fetchGameBySLug,
    enabled: !!slug,
    staleTime: 5 * 1000,
    refetchOnMount,
    refetchOnWindowFocus: false,
  });

  function handleInactiveGames() {
    if (!data || data?.isActive == true) {
      return;
    }

    push('/casino');
  }

  useEffect(handleInactiveGames, [data, push]);

  const gameStatus: Either<string, undefined> = useMemo(() => {
    const aggregatorStatus =
      aggregators
        ?.find(
          aggregator =>
            aggregator?.id?.toString() === data?.aggregator?.id?.toString?.(),
        )
        ?.status?.id?.toString?.() ??
      GamingAggregatorStatusId.Active?.toString?.();

    return aggregatorStatus;
  }, [data, aggregators]);

  return {
    game: data,
    isLoading: isPending && fetchStatus !== 'idle',
    error,
    isError,
    refetch,
    gameStatus,
  };
}
